import { forwardRef, memo, useImperativeHandle, useState } from 'react';
import { Image, Space, Tooltip } from 'antd';
import { EyeIcon } from 'assets/svg';
import TableComponent from 'components/Table/TableComponent';
import Content from 'layouts/Content';
import { useNavigate } from 'react-router-dom';
import formatNumber, { formatDate } from 'utils/constants/functions';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import useQueryParam from 'hooks/useQueryPrams';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';
import path from 'routes/Path';

const { FileImageOutlined } = icons;

interface IProps {
  data: any;
  isLoading: boolean;
  refetch: any;
  filterData: any;
}

// const TableProduct =

const TableProduct = memo(
  forwardRef(({ data, isLoading, refetch, filterData }: IProps, ref: any) => {
    const navigate = useNavigate();

    const [imageError, setImageError] = useState<Record<string, boolean>>({});

    const queryParam = useQueryParam(),
      page = parseInt(queryParam.get('page') + '') || 1,
      page_size =
        parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

    useImperativeHandle(ref, () => ({
      handleExportExcel,
    }));

    const handleExportExcel = () => {
      // Tạo columns mới với format số đúng định dạng cho export
      const exportColumns = columns.map((col: any) => {
        // Format các cột số - sử dụng type: 'number' với unit để Excel tự format với dấu phẩy
        if (col.dataIndex === 'age' || col.dataIndex === 'priced') {
          return {
            ...col,
            type: 'number',
            unit: 1, // Cần có unit để Excel format với #,##0 (dấu phẩy ngăn cách hàng nghìn)
            // Xóa render function để Excel tự format
            render: undefined,
          };
        }
        return col;
      });

      refetch({
        ...filterData,
        skip: 0,
        take: data?.pageInfo.totalItems,
        types: 'export',
        headerRow: exportColumns,
      });
    };

    const handleImageError = (id: string) => {
      setImageError((prev) => ({ ...prev, [id]: true }));
    };

    const columns: ColumnsTypeCustom = [
      {
        title: 'STT',
        width: 70,
        align: 'center',
        render: (_, __, index) => index + 1 + page_size * (page - 1),
      },
      {
        title: 'Mã sản phẩm',
        dataIndex: 'id',
        align: 'left',
        width: 200,
      },
      {
        title: 'Hình ảnh',
        width: 100,
        align: 'center',
        render: (record) => {
          const isImageInvalid = !record?.urlImage1 || imageError[record?.id];

          if (isImageInvalid) {
            return (
              <FileImageOutlined
                style={{
                  height: 50,
                  width: 50,
                  fontSize: 24,
                  justifyContent: 'center',
                }}
              />
            );
          }

          return (
            <Image
              style={{ height: 50, width: 50 }}
              src={record?.urlImage1}
              alt={record?.name}
              onError={() => handleImageError(record?.id)}
            />
          );
        },
      },
      {
        title: 'Tên sản phẩm',
        dataIndex: 'name',
        align: 'left',
        width: 300,
        render: (value) => value || '-',
      },
      {
        title: 'Dòng hàng SAP',
        dataIndex: 'commodityLine',
        align: 'left',
        width: 130,
        render: (value) => value || '-',
      },
      {
        title: 'Loại sản phẩm',
        dataIndex: ['ttype', 'name'],
        width: 130,
        align: 'left',
        render: (value) => value || '-',
      },
      {
        title: 'Chủng loại',
        width: 120,
        dataIndex: 'species',
        align: 'left',
      },
      {
        title: 'ĐVT',
        dataIndex: 'unit',
        align: 'left',
        width: 100,
        render: (value) => value || '-',
      },
      {
        title: 'Tuổi vàng',
        dataIndex: 'age',
        width: 100,
        align: 'right',
        render: (value) => value || '-',
      },
      {
        title: 'Giá hao lai',
        dataIndex: 'priced',
        width: 100,
        align: 'right',
        render: (value) => value || '-',
      },
      {
        title: 'Bộ sưu tập',
        dataIndex: 'collection',
        width: 100,
        align: 'left',
        render: (value) => value || '-',
      },
      {
        title: 'Mã MRP',
        dataIndex: 'mrpId',
        width: 100,
        align: 'left',
        render: (value) => value || '-',
      },
      {
        title: 'Tên sản phẩm MRP',
        dataIndex: 'mrpName',
        width: 150,
        align: 'left',
        render: (value) => value || '-',
      },
      {
        title: 'Người tạo',
        dataIndex: ['tcreateUser', 'fullName'],
        align: 'left',
        width: 150,
        render: (value) => value || '-',
      },
      {
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        width: 150,
        align: 'left',
        type: 'date',
        render: (value) =>
          value ? formatDate(value, 'DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
        align: 'left',
        width: 150,
        render: (value) => value || '-',
      },
      {
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        align: 'left',
        width: 150,
        type: 'date',
        render: (value) => formatDate(value, 'DD/MM/YYYY HH:mm:ss') || '-',
      },
      {
        title: <div className="text-center">Thao tác</div>,
        fixed: 'right',
        width: 100,
        align: 'center',
        render: (_: any, record: any) => {
          return (
            <Space size={'middle'} className="text-lg ">
              <Tooltip placement="top" title={'Xem chi tiết'}>
                <EyeIcon
                  onClick={() => {
                    navigate(path.PRODUCT_DETAIL, { state: record });
                  }}
                  className="cursor-pointer"
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ];

    return (
      <Content>
        <TableComponent
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          scroll={{ x: 1500 }}
          pagination={{
            current: data?.pageInfo?.currentPage,
            pageSize: data?.pageInfo?.itemsPerPage,
            total: data?.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Content>
    );
  }),
);

export default TableProduct;
